import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, DollarSign, ShoppingCart, TrendingUp, Calendar,
  ChevronLeft, ChevronRight, Loader2, FileText, CreditCard,
  Banknote, Receipt, Clock, Download
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';

const PAYMENT_ICONS = { cash: Banknote, card: CreditCard, split: Receipt };

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this_week', label: 'This Week' },
  { id: 'last_week', label: 'Last Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'this_year', label: 'This Year' },
  { id: 'custom', label: 'Custom Range' },
];

function getPresetDates(id) {
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  const dow = now.getDay() || 7; // Mon=1

  switch (id) {
    case 'today': return { from: today, to: today };
    case 'yesterday': { const yd = new Date(y, m, d - 1); return { from: yd.toISOString().split('T')[0], to: yd.toISOString().split('T')[0] }; }
    case 'this_week': { const mon = new Date(y, m, d - dow + 1); return { from: mon.toISOString().split('T')[0], to: today }; }
    case 'last_week': { const lm = new Date(y, m, d - dow - 6); const ls = new Date(y, m, d - dow); return { from: lm.toISOString().split('T')[0], to: ls.toISOString().split('T')[0] }; }
    case 'this_month': return { from: `${y}-${String(m + 1).padStart(2, '0')}-01`, to: today };
    case 'last_month': { const lms = new Date(y, m - 1, 1); const lme = new Date(y, m, 0); return { from: lms.toISOString().split('T')[0], to: lme.toISOString().split('T')[0] }; }
    case 'this_year': return { from: `${y}-01-01`, to: today };
    default: return { from: today, to: today };
  }
}

const ReportsPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [activeTab, setActiveTab] = useState('daily');
  const [preset, setPreset] = useState('today');
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyReport, setDailyReport] = useState(null);
  const [rangeReport, setRangeReport] = useState(null);

  useEffect(() => { fetchShop(); }, []);

  useEffect(() => {
    if (shop) {
      if (activeTab === 'daily') fetchDailyReport();
      else fetchRangeReport();
    }
  }, [shop, fromDate, toDate, activeTab]);

  const fetchShop = async () => {
    try {
      const res = await api.get('/shops');
      if (res.data.length === 0) { navigate('/shop/setup'); return; }
      setShop(res.data[0]);
    } catch { console.error('Failed to load shop'); }
  };

  const fetchDailyReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/shops/${shop.id}/reports/daily?date=${fromDate}`);
      setDailyReport(res.data);
    } catch (e) { console.error('Report error:', e); }
    finally { setLoading(false); }
  };

  const fetchRangeReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/shops/${shop.id}/reports/range?from_date=${fromDate}&to_date=${toDate}`);
      setRangeReport(res.data);
    } catch (e) { console.error('Range report error:', e); }
    finally { setLoading(false); }
  };

  const handlePresetChange = (id) => {
    setPreset(id);
    if (id !== 'custom') {
      const dates = getPresetDates(id);
      setFromDate(dates.from);
      setToDate(dates.to);
      if (id === 'today' || id === 'yesterday') setActiveTab('daily');
      else setActiveTab('range');
    }
  };

  const changeDailyDate = (delta) => {
    const d = new Date(fromDate);
    d.setDate(d.getDate() + delta);
    const newDate = d.toISOString().split('T')[0];
    setFromDate(newDate);
    setToDate(newDate);
    setPreset('custom');
  };

  const isToday = fromDate === new Date().toISOString().split('T')[0];
  const maxHourlyRevenue = dailyReport ? Math.max(...dailyReport.hourly_breakdown.map(h => h.revenue), 1) : 1;

  if (loading && !dailyReport && !rangeReport) {
    return <ShopLayout><div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div></ShopLayout>;
  }

  return (
    <ShopLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('reports_title') || 'Reports'}</h2>
          <p className="text-slate-500 text-sm">Sales analytics and End of Day reports</p>
        </div>

        {/* Period Presets */}
        <div className="flex flex-wrap gap-2" data-testid="period-presets">
          {PRESETS.map(p => (
            <button key={p.id} onClick={() => handlePresetChange(p.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                preset === p.id ? 'bg-[#1e3a5f] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`preset-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Date Pickers */}
        <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-lg p-3" data-testid="date-range-picker">
          {activeTab === 'daily' ? (
            <>
              <button onClick={() => changeDailyDate(-1)} className="p-2 hover:bg-slate-100 rounded" data-testid="prev-day-btn">
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#1e3a5f]" />
                <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setToDate(e.target.value); setPreset('custom'); }}
                  max={new Date().toISOString().split('T')[0]}
                  className="px-3 py-2 border border-slate-200 rounded-lg focus:border-[#1e3a5f] focus:outline-none font-medium text-[#1e3a5f]"
                  data-testid="date-input" />
                {isToday && <span className="px-2 py-1 bg-[#22c55e]/10 text-[#22c55e] text-xs font-medium rounded">Today</span>}
              </div>
              <button onClick={() => changeDailyDate(1)} disabled={isToday}
                className="p-2 hover:bg-slate-100 rounded disabled:opacity-30" data-testid="next-day-btn">
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">From:</span>
                <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPreset('custom'); }}
                  className="px-3 py-2 border border-slate-200 rounded-lg focus:border-[#1e3a5f] focus:outline-none text-sm font-medium text-[#1e3a5f]"
                  data-testid="from-date-input" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">To:</span>
                <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPreset('custom'); }}
                  max={new Date().toISOString().split('T')[0]}
                  className="px-3 py-2 border border-slate-200 rounded-lg focus:border-[#1e3a5f] focus:outline-none text-sm font-medium text-[#1e3a5f]"
                  data-testid="to-date-input" />
              </div>
            </>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={() => setActiveTab('daily')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${activeTab === 'daily' ? 'bg-[#22c55e]/10 text-[#22c55e]' : 'text-slate-500 hover:bg-slate-50'}`}
              data-testid="tab-daily"
            >
              <Calendar className="w-4 h-4 inline mr-1" /> Daily
            </button>
            <button onClick={() => setActiveTab('range')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${activeTab === 'range' ? 'bg-[#22c55e]/10 text-[#22c55e]' : 'text-slate-500 hover:bg-slate-50'}`}
              data-testid="tab-range"
            >
              <BarChart3 className="w-4 h-4 inline mr-1" /> Range
            </button>
          </div>
        </div>

        {/* Daily Report */}
        {activeTab === 'daily' && dailyReport && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Revenue', value: `€${dailyReport.total_revenue.toFixed(2)}`, icon: DollarSign, color: 'bg-[#22c55e]/10 text-[#22c55e]' },
                { label: 'Sales', value: dailyReport.completed_sales, icon: ShoppingCart, color: 'bg-blue-50 text-blue-600' },
                { label: 'Avg Sale', value: `€${dailyReport.average_sale.toFixed(2)}`, icon: TrendingUp, color: 'bg-[#1e3a5f]/5 text-[#1e3a5f]' },
                { label: 'Invoices', value: dailyReport.invoices_count, icon: FileText, color: 'bg-amber-50 text-amber-600' },
              ].map((card, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`report-stat-${i}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.color}`}>
                      <card.icon className="w-5 h-5" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-[#1e3a5f]">{card.value}</p>
                  <p className="text-sm text-slate-500">{card.label}</p>
                </motion.div>
              ))}
            </div>

            {/* Payment Methods */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="font-semibold text-[#1e3a5f] mb-3">Payment Methods</h3>
              {Object.keys(dailyReport.payment_split).length === 0 ? (
                <p className="text-sm text-slate-400">No sales for this day</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(dailyReport.payment_split).map(([method, data]) => {
                    const PIcon = PAYMENT_ICONS[method] || CreditCard;
                    return (
                      <div key={method} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <PIcon className="w-5 h-5 text-[#1e3a5f]" />
                        <div>
                          <p className="text-sm font-medium text-[#1e3a5f] capitalize">{method}</p>
                          <p className="text-xs text-slate-500">{data.count} sales — €{data.amount.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Hourly Breakdown */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="font-semibold text-[#1e3a5f] mb-3">Hourly Breakdown</h3>
              <div className="flex items-end gap-[2px] h-32" data-testid="hourly-chart">
                {dailyReport.hourly_breakdown.map((h, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center group relative">
                    <div className="w-full bg-[#22c55e]/20 hover:bg-[#22c55e]/40 transition-colors rounded-t"
                      style={{ height: `${Math.max(2, (h.revenue / maxHourlyRevenue) * 100)}%` }}>
                      <div className="opacity-0 group-hover:opacity-100 absolute -top-12 left-1/2 -translate-x-1/2 bg-[#1e3a5f] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                        {h.count} sales — €{h.revenue.toFixed(0)}
                      </div>
                    </div>
                    {i % 3 === 0 && <span className="text-[10px] text-slate-400 mt-1">{String(h.hour).padStart(2, '0')}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Top Products */}
            <div className="bg-white border border-slate-200 rounded-lg">
              <div className="p-4 border-b border-slate-100">
                <h3 className="font-semibold text-[#1e3a5f]">Top Products</h3>
              </div>
              {dailyReport.top_products.length === 0 ? (
                <div className="p-4 text-center text-slate-400 text-sm">No sales for this day</div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {dailyReport.top_products.map((p, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-[#1e3a5f]/5 rounded flex items-center justify-center text-xs font-bold text-[#1e3a5f]">{i + 1}</span>
                        <span className="font-medium text-[#1e3a5f]">{p.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-[#22c55e]">€{p.revenue.toFixed(2)}</span>
                        <span className="text-xs text-slate-400 ml-2">x{p.quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Range Report */}
        {activeTab === 'range' && rangeReport && (
          <div className="space-y-4">
            {/* Period Header */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <p className="text-sm text-slate-500 mb-1">{rangeReport.from_date} to {rangeReport.to_date} ({rangeReport.num_days} days)</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total Revenue', value: `€${rangeReport.total_revenue.toFixed(2)}`, icon: DollarSign, color: 'bg-[#22c55e]/10 text-[#22c55e]' },
                { label: 'Total Sales', value: rangeReport.total_sales, icon: ShoppingCart, color: 'bg-blue-50 text-blue-600' },
                { label: 'Daily Average', value: `€${rangeReport.daily_average_revenue.toFixed(2)}`, icon: TrendingUp, color: 'bg-[#1e3a5f]/5 text-[#1e3a5f]' },
                { label: 'Avg per Sale', value: `€${rangeReport.average_sale.toFixed(2)}`, icon: Receipt, color: 'bg-amber-50 text-amber-600' },
              ].map((card, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`range-stat-${i}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.color} mb-2`}>
                    <card.icon className="w-5 h-5" />
                  </div>
                  <p className="text-2xl font-bold text-[#1e3a5f]">{card.value}</p>
                  <p className="text-sm text-slate-500">{card.label}</p>
                </motion.div>
              ))}
            </div>

            {/* Payment Methods */}
            {Object.keys(rangeReport.payment_split).length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h3 className="font-semibold text-[#1e3a5f] mb-3">Payment Methods</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(rangeReport.payment_split).map(([method, data]) => {
                    const PIcon = PAYMENT_ICONS[method] || CreditCard;
                    return (
                      <div key={method} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <PIcon className="w-5 h-5 text-[#1e3a5f]" />
                        <div>
                          <p className="text-sm font-medium text-[#1e3a5f] capitalize">{method}</p>
                          <p className="text-xs text-slate-500">{data.count} sales — €{data.amount.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Daily Breakdown Chart */}
            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="range-chart">
              <h3 className="font-semibold text-[#1e3a5f] mb-4">Daily Breakdown</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {rangeReport.days.map(day => {
                  const maxRev = Math.max(...rangeReport.days.map(d => d.revenue), 1);
                  const pct = (day.revenue / maxRev) * 100;
                  return (
                    <div key={day.date} className="flex items-center gap-3">
                      <span className="w-20 text-xs font-medium text-slate-500 flex-shrink-0">{day.date}</span>
                      <span className="w-8 text-xs text-slate-400 flex-shrink-0">{day.day_name}</span>
                      <div className="flex-1 h-7 bg-slate-50 rounded overflow-hidden relative">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.4 }}
                          className="h-full bg-[#22c55e]/30 rounded" />
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="text-xs text-[#1e3a5f]">{day.sales_count} sales</span>
                        </div>
                      </div>
                      <span className="w-20 text-right text-sm font-semibold text-[#22c55e] flex-shrink-0">€{day.revenue.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Products */}
            {rangeReport.top_products.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="font-semibold text-[#1e3a5f]">Top Products</h3>
                </div>
                <div className="divide-y divide-slate-50">
                  {rangeReport.top_products.map((p, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-[#1e3a5f]/5 rounded flex items-center justify-center text-xs font-bold text-[#1e3a5f]">{i + 1}</span>
                        <span className="font-medium text-[#1e3a5f]">{p.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-[#22c55e]">€{p.revenue.toFixed(2)}</span>
                        <span className="text-xs text-slate-400 ml-2">x{p.quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

export default ReportsPage;
